import React, { useState, useEffect } from 'react';

const SimpleDoctorDashboard = () => {
  const [activeTab, setActiveTab] = useState('services');
  const [selectedDate, setSelectedDate] = useState('2025-08-02');
  const [availability, setAvailability] = useState({});
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [operatingHours, setOperatingHours] = useState({
    opening_time: '08:00',
    closing_time: '17:00',
    break_start: '12:00',
    break_end: '14:00'
  });

  // Generate time slots based on operating hours
  const generateTimeSlots = () => {
    const slots = [];
    const start = parseInt(operatingHours.opening_time.split(':')[0]);
    const end = parseInt(operatingHours.closing_time.split(':')[0]);
    const breakStart = parseInt(operatingHours.break_start.split(':')[0]);
    const breakEnd = parseInt(operatingHours.break_end.split(':')[0]);

    for (let hour = start; hour < end; hour++) {
      if (hour >= breakStart && hour < breakEnd) continue; // Skip break time
      
      ['00', '30'].forEach(minute => {
        const time = `${hour.toString().padStart(2, '0')}:${minute}`;
        slots.push(time);
      });
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Next 7 days only
  const generateNext7Days = () => {
    const days = [];
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      days.push({ date: dateStr, display: dayName, isToday: i === 0 });
    }
    return days;
  };

  const next7Days = generateNext7Days();

  // Mock doctor data
  const doctorInfo = {
    id: 'demo-doctor-001',
    name: 'Dr. Sarah Al-Ahmad',
    speciality: 'Cardiology',
    clinic: 'Heart Care Center'
  };

  // Mock doctor data and services
  useEffect(() => {
    const mockServices = [
      {
        id: 'consultation-1',
        name: 'Initial Consultation',
        duration: 30,
        price: 35,
        description: 'Comprehensive health assessment and diagnosis',
        active: true
      },
      {
        id: 'consultation-2',
        name: 'Follow-up Consultation',
        duration: 20,
        price: 25,
        description: 'Follow-up visit for ongoing treatment',
        active: true
      },
      {
        id: 'procedure-1',
        name: 'ECG Test',
        duration: 45,
        price: 45,
        description: 'Electrocardiogram heart monitoring',
        active: true
      },
      {
        id: 'procedure-2',
        name: 'Blood Pressure Monitoring',
        duration: 15,
        price: 15,
        description: '24-hour blood pressure monitoring setup',
        active: true
      }
    ];

    const mockAvailability = {
      'consultation-1': {
        '2025-08-02': ['09:00', '09:30', '10:00', '14:00', '15:00'],
        '2025-08-03': ['08:00', '10:00', '16:00'],
      },
      'consultation-2': {
        '2025-08-02': ['09:30', '10:30', '14:30', '15:30'],
        '2025-08-03': ['08:30', '10:30', '16:30'],
      }
    };
    
    const mockAppointments = [
      {
        id: 1,
        date: '2025-08-02',
        time: '10:00',
        patient: 'Ahmed Al-Rashid',
        service: 'Initial Consultation',
        service_id: 'consultation-1',
        duration: 30,
        symptoms: 'Chest pain, shortness of breath',
        appointment_type: 'New User'
      },
      {
        id: 2,
        date: '2025-08-03',
        time: '10:00',
        patient: 'Fatima Hassan',
        service: 'Follow-up Consultation',
        service_id: 'consultation-2',
        duration: 20,
        symptoms: 'Blood pressure monitoring',
        appointment_type: 'Follow-up'
      }
    ];

    setServices(mockServices);
    setAvailability(mockAvailability);
    setAppointments(mockAppointments);
  }, []);

  const isTimeAvailable = (time) => {
    if (!selectedService) return false;
    return availability[selectedService]?.[selectedDate]?.includes(time) || false;
  };

  const isTimeBooked = (time) => {
    return appointments.some(apt => apt.date === selectedDate && apt.time === time);
  };

  const getConflictingSlots = (time, duration) => {
    const slots = [];
    const [hour, minute] = time.split(':').map(Number);
    const startMinutes = hour * 60 + minute;
    
    for (let i = 0; i < duration; i += 30) {
      const slotMinutes = startMinutes + i;
      const slotHour = Math.floor(slotMinutes / 60);
      const slotMinute = slotMinutes % 60;
      const slotTime = `${slotHour.toString().padStart(2, '0')}:${slotMinute.toString().padStart(2, '0')}`;
      slots.push(slotTime);
    }
    return slots;
  };

  const toggleTimeSlot = (time) => {
    if (!selectedService) return;
    
    const service = services.find(s => s.id === selectedService);
    if (!service) return;

    const currentSlots = availability[selectedService]?.[selectedDate] || [];
    const conflictSlots = getConflictingSlots(time, service.duration);
    
    if (currentSlots.includes(time)) {
      // Remove time slot and all its duration slots
      const newSlots = currentSlots.filter(slot => !conflictSlots.includes(slot));
      setAvailability({
        ...availability,
        [selectedService]: {
          ...availability[selectedService],
          [selectedDate]: newSlots
        }
      });
    } else {
      // Check if any conflicting slot is booked
      const hasBookedConflict = conflictSlots.some(slot => 
        appointments.some(apt => apt.date === selectedDate && apt.time === slot)
      );
      
      if (hasBookedConflict) {
        alert('Cannot set availability - conflicts with existing user appointment');
        return;
      }
      
      // Add all required time slots for this service
      const newSlots = [...new Set([...currentSlots, ...conflictSlots])].sort();
      setAvailability({
        ...availability,
        [selectedService]: {
          ...availability[selectedService],
          [selectedDate]: newSlots
        }
      });
    }
  };

  const addService = () => {
    const serviceName = prompt('Enter consultation/procedure name:');
    const serviceDuration = parseInt(prompt('Enter duration in minutes (15, 30, 45, 60):') || '30');
    const servicePrice = parseFloat(prompt('Enter consultation fee in KD:') || '30');
    
    if (serviceName) {
      const newService = {
        id: `service-${Date.now()}`,
        name: serviceName,
        duration: serviceDuration,
        price: servicePrice,
        description: '',
        active: true
      };
      setServices([...services, newService]);
    }
  };

  const toggleServiceActive = (serviceId) => {
    setServices(services.map(service => 
      service.id === serviceId 
        ? { ...service, active: !service.active }
        : service
    ));
  };

  const updateOperatingHours = () => {
    const opening = prompt('Clinic opening time (HH:MM):', operatingHours.opening_time);
    const closing = prompt('Clinic closing time (HH:MM):', operatingHours.closing_time);
    const breakStart = prompt('Lunch break start (HH:MM):', operatingHours.break_start);
    const breakEnd = prompt('Lunch break end (HH:MM):', operatingHours.break_end);
    
    setOperatingHours({
      opening_time: opening || operatingHours.opening_time,
      closing_time: closing || operatingHours.closing_time,
      break_start: breakStart || operatingHours.break_start,
      break_end: breakEnd || operatingHours.break_end
    });
  };

  const saveChanges = () => {
    alert('✅ Doctor availability saved successfully!');
    console.log('Saved doctor availability:', availability);
    console.log('Doctor operating hours:', operatingHours);
    console.log('Doctor services:', services);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-lg mx-auto px-4 py-4">
          <h1 className="text-xl font-bold text-gray-900">Dr. Dashboard</h1>
          <p className="text-sm text-gray-600">{doctorInfo.name} - {doctorInfo.speciality}</p>
          <p className="text-xs text-gray-500">{doctorInfo.clinic}</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Enhanced Tab Navigation */}
        <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
          <button
            onClick={() => setActiveTab('services')}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'services'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600'
            }`}
          >
            🩺 Services
          </button>
          <button
            onClick={() => setActiveTab('availability')}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'availability'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600'
            }`}
          >
            📅 Schedule
          </button>
          <button
            onClick={() => setActiveTab('appointments')}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'appointments'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600'
            }`}
          >
            👥 Users
          </button>
        </div>

        {/* Medical Services Management Tab */}
        {activeTab === 'services' && (
          <div>
            {/* Clinic Operating Hours */}
            <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-base font-semibold text-gray-900">Clinic Hours</h3>
                <button
                  onClick={updateOperatingHours}
                  className="text-blue-600 hover:text-blue-700 text-sm"
                >
                  Edit
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Open:</span> {operatingHours.opening_time}
                </div>
                <div>
                  <span className="font-medium">Close:</span> {operatingHours.closing_time}
                </div>
                <div>
                  <span className="font-medium">Lunch:</span> {operatingHours.break_start} - {operatingHours.break_end}
                </div>
              </div>
            </div>

            {/* Medical Services List */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-base font-semibold text-gray-900">Medical Services ({services.length})</h3>
                <button
                  onClick={addService}
                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                >
                  + Add Service
                </button>
              </div>
              
              <div className="space-y-3">
                {services.map((service) => (
                  <div key={service.id} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900">{service.name}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            service.active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {service.active ? 'Available' : 'Unavailable'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{service.description || 'No description'}</p>
                        <div className="flex gap-4 text-xs text-gray-500">
                          <span>⏱️ {service.duration} min</span>
                          <span>💰 {service.price} KD</span>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleServiceActive(service.id)}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          service.active
                            ? 'bg-red-50 text-red-700 hover:bg-red-100'
                            : 'bg-green-50 text-green-700 hover:bg-green-100'
                        }`}
                      >
                        {service.active ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Medical Services Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 text-sm mb-2">🩺 Medical Service Management:</h4>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>1. Set your clinic operating hours</li>
                <li>2. Add consultations and procedures with accurate durations</li>
                <li>3. Set appropriate consultation fees for each service</li>
                <li>4. Enable/disable services based on your expertise</li>
                <li>5. Configure availability for each service separately</li>
              </ul>
            </div>
          </div>
        )}

        {/* Medical Service-Based Availability Tab */}
        {activeTab === 'availability' && (
          <div>
            {/* Service Selector */}
            <div className="mb-6">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Select Medical Service</h3>
              <div className="grid grid-cols-1 gap-2">
                {services.filter(s => s.active).map((service) => (
                  <button
                    key={service.id}
                    onClick={() => setSelectedService(service.id)}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      selectedService === service.id
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-gray-900">{service.name}</h4>
                        <p className="text-sm text-gray-600">{service.duration} min • {service.price} KD</p>
                      </div>
                      {selectedService === service.id && (
                        <div className="text-blue-600">✓</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {selectedService && (
              <div>
                {/* Simple Day Selector */}
                <div className="mb-6">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">Choose Clinic Day</h3>
                  <div className="grid grid-cols-7 gap-2">
                    {next7Days.map((day) => (
                      <button
                        key={day.date}
                        onClick={() => setSelectedDate(day.date)}
                        className={`p-2 rounded-lg text-xs font-medium transition-colors ${
                          selectedDate === day.date
                            ? 'bg-blue-600 text-white'
                            : day.isToday
                            ? 'bg-green-100 text-green-800'
                            : 'bg-white text-gray-700 border border-gray-200'
                        }`}
                      >
                        {day.display}
                        {day.isToday && <div className="text-xs mt-1">Today</div>}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Enhanced Time Grid */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-base font-semibold text-gray-900">
                      Schedule: {services.find(s => s.id === selectedService)?.name}
                    </h3>
                    <button
                      onClick={saveChanges}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                    >
                      Save Schedule
                    </button>
                  </div>

                  {/* Morning Clinic */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">🌅 Morning Clinic</h4>
                    <div className="grid grid-cols-4 gap-2">
                      {timeSlots.filter(time => parseInt(time.split(':')[0]) < 12).map((time) => {
                        const available = isTimeAvailable(time);
                        const booked = isTimeBooked(time);
                        const service = services.find(s => s.id === selectedService);
                        const conflictSlots = getConflictingSlots(time, service?.duration || 30);
                        const hasConflict = conflictSlots.some(slot => 
                          appointments.some(apt => apt.date === selectedDate && apt.time === slot)
                        );
                        
                        return (
                          <button
                            key={time}
                            onClick={() => !booked && !hasConflict && toggleTimeSlot(time)}
                            disabled={booked || hasConflict}
                            className={`p-3 rounded-lg text-sm font-medium transition-colors ${
                              booked || hasConflict
                                ? 'bg-red-100 text-red-700 border border-red-200'
                                : available
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : 'bg-white text-gray-700 border border-gray-200 hover:bg-blue-50'
                            }`}
                          >
                            {time}
                            {booked && <div className="text-xs mt-1">User</div>}
                            {hasConflict && !booked && <div className="text-xs mt-1">Conflict</div>}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Afternoon Clinic */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">🌇 Afternoon Clinic</h4>
                    <div className="grid grid-cols-4 gap-2">
                      {timeSlots.filter(time => parseInt(time.split(':')[0]) >= 12).map((time) => {
                        const available = isTimeAvailable(time);
                        const booked = isTimeBooked(time);
                        const service = services.find(s => s.id === selectedService);
                        const conflictSlots = getConflictingSlots(time, service?.duration || 30);
                        const hasConflict = conflictSlots.some(slot => 
                          appointments.some(apt => apt.date === selectedDate && apt.time === slot)
                        );
                        
                        return (
                          <button
                            key={time}
                            onClick={() => !booked && !hasConflict && toggleTimeSlot(time)}
                            disabled={booked || hasConflict}
                            className={`p-3 rounded-lg text-sm font-medium transition-colors ${
                              booked || hasConflict
                                ? 'bg-red-100 text-red-700 border border-red-200'
                                : available
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : 'bg-white text-gray-700 border border-gray-200 hover:bg-blue-50'
                            }`}
                          >
                            {time}
                            {booked && <div className="text-xs mt-1">User</div>}
                            {hasConflict && !booked && <div className="text-xs mt-1">Conflict</div>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Doctor-specific Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 text-sm mb-2">🩺 Medical Scheduling:</h4>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>1. Select a medical service above</li>
                    <li>2. Choose your clinic day</li>
                    <li>3. Set consultation hours (green = available)</li>
                    <li>4. Red slots = booked users or conflicts</li>
                    <li>5. System prevents user appointment overlaps</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'appointments' && (
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-4">
              Today's Users ({appointments.length})
            </h3>
            
            {appointments.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
                <div className="text-4xl mb-2">🩺</div>
                <h4 className="font-medium text-gray-900 mb-1">No users scheduled</h4>
                <p className="text-sm text-gray-600">Configure your services and schedule first!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {appointments.map((appointment) => (
                  <div key={appointment.id} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">{appointment.patient}</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          📅 {new Date(appointment.date).toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </p>
                        <p className="text-sm text-gray-600">⏰ {appointment.time} ({appointment.duration} min)</p>
                        <p className="text-sm text-gray-600">🩺 {appointment.service}</p>
                      </div>
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                        {appointment.appointment_type}
                      </span>
                    </div>
                    
                    {/* Patient symptoms/chief complaint */}
                    <div className="bg-gray-50 rounded-lg p-3 mt-3">
                      <h5 className="text-xs font-medium text-gray-700 mb-1">Chief Complaint:</h5>
                      <p className="text-sm text-gray-600">{appointment.symptoms}</p>
                    </div>
                    
                    {/* Medical action buttons */}
                    <div className="flex gap-2 mt-3">
                      <button className="flex-1 bg-blue-50 text-blue-700 px-3 py-2 rounded text-xs font-medium hover:bg-blue-100 transition-colors">
                        📋 Medical Records
                      </button>
                      <button className="flex-1 bg-green-50 text-green-700 px-3 py-2 rounded text-xs font-medium hover:bg-green-100 transition-colors">
                        💬 Message User
                      </button>
                      <button className="flex-1 bg-purple-50 text-purple-700 px-3 py-2 rounded text-xs font-medium hover:bg-purple-100 transition-colors">
                        📅 Add to Calendar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleDoctorDashboard;